import React, { useState, useMemo } from 'react';
import { Edit, Trash2, Phone, Mail, Globe, Star } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';
import { formatDate } from '../../../utils/formatters';
import { usePreferences } from '../../../hooks/usePreferences';

const SupplierTable = ({ suppliers, selectedSupplier, onSupplierSelect, onSupplierEdit, onSupplierDelete, canDelete }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const { preferences } = usePreferences();
  const preferredFormat = preferences?.date_format || 'DD/MM/YYYY';

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-success/10 text-success border-success/20',
      pending: 'bg-warning/10 text-warning border-warning/20',
      inactive: 'bg-muted text-muted-foreground border-muted'
    };
    return colors?.[status] || colors?.inactive;
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedSuppliers = React.useMemo(() => {
    let sortableSuppliers = [...suppliers];
    if (sortConfig?.key) {
      sortableSuppliers?.sort((a, b) => {
        if (a?.[sortConfig?.key] < b?.[sortConfig?.key]) {
          return sortConfig?.direction === 'asc' ? -1 : 1;
        }
        if (a?.[sortConfig?.key] > b?.[sortConfig?.key]) {
          return sortConfig?.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableSuppliers;
  }, [suppliers, sortConfig]);

  const formatCurrency = (value) => {
    if (!value) return '$0';
    return value;
  };

  // using shared formatDate util with user preference

  const renderRating = (rating) => {
    const stars = Array?.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={cn(
          "w-3 h-3",
          index < Math?.floor(rating) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"
        )}
      />
    ));
    return (
      <div className="flex items-center gap-1">
        {stars}
        <span className="text-xs text-muted-foreground ml-1">({rating})</span>
      </div>
    );
  };

  if (!suppliers || suppliers?.length === 0) {
    return (
      <div className="bg-card border rounded-lg p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Globe className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Suppliers Found</h3>
          <p className="text-muted-foreground mb-4">
            No suppliers match your current filters. Try adjusting your search criteria.
          </p>
          <Button variant="outline" onClick={() => window?.location?.reload()}>
            Reset Filters
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
      <div className="overflow-auto max-h-[calc(100vh-250px)] relative">
        <table className="min-w-full divide-y divide-border/60 dark:divide-border/35">
          <thead className="bg-muted sticky top-0 z-10 shadow-sm">
            <tr>
              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('company_name')}
                  className="flex items-center gap-2 hover:text-gray-700 transition-colors"
                >
                  Company
                  {sortConfig?.key === 'company_name' && (
                    <span className="text-xs">
                      {sortConfig?.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </button>
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Contact Person
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-2 hover:text-gray-700 transition-colors"
                >
                  Status
                  {sortConfig?.key === 'status' && (
                    <span className="text-xs">
                      {sortConfig?.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </button>
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('rating')}
                  className="flex items-center gap-2 hover:text-gray-700 transition-colors"
                >
                  Rating
                  {sortConfig?.key === 'rating' && (
                    <span className="text-xs">
                      {sortConfig?.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </button>
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('totalAssetValue')}
                  className="flex items-center gap-2 hover:text-gray-700 transition-colors"
                >
                  Total Asset Value
                  {sortConfig?.key === 'totalAssetValue' && (
                    <span className="text-xs">
                      {sortConfig?.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </button>
              </th>
              <th scope="col" className="px-6 py-4 text-center text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border/50 dark:divide-border/30">
            {sortedSuppliers?.map((supplier) => (
              <tr
                key={supplier?.id}
                className={cn(
                  "hover:bg-muted/80 transition-colors cursor-pointer group",
                  selectedSupplier?.id === supplier?.id && "bg-primary/10"
                )}
                onClick={() => onSupplierSelect(supplier)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    {supplier?.image_url ? (
                      <img
                        src={supplier?.image_url}
                        alt=""
                        className="w-10 h-10 rounded-lg object-cover border border-border"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center border border-border">
                        <Globe className="w-5 h-5 text-muted-foreground/70" />
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-bold text-foreground flex items-center gap-2">
                        {supplier?.company_name}
                        {supplier?.preferred_vendor && (
                          <div className="w-2 h-2 bg-primary rounded-full" title="Preferred Vendor" />
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-bold text-foreground">{supplier?.contact_person}</div>
                    <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground/70 mt-0.5">
                      <Mail className="w-3 h-3" />
                      {supplier?.email}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={cn(
                      "inline-flex px-2.5 py-1 rounded-full text-[11px] font-bold border capitalize",
                      getStatusColor(supplier?.status)
                    )}
                  >
                    {supplier?.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {renderRating(supplier?.rating)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-bold text-foreground">
                      {new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(supplier?.totalAssetValue || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground/70 font-medium">
                      {supplier?.totalAssetsCount || 0} Assets
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e?.stopPropagation()}>
                  <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onSupplierEdit(supplier)}
                      className="h-8 w-8 text-muted-foreground/70 hover:text-primary hover:bg-primary/10 dark:hover:bg-primary/20"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onSupplierDelete(supplier?.id)}
                        className="h-8 w-8 text-muted-foreground/70 hover:text-destructive hover:bg-destructive/10 dark:hover:bg-destructive/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SupplierTable;